import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Action, Store, createSelector } from '@ngrx/store';

import { QuillEditorComponent, QuillModules } from 'ngx-quill';

import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { selectBlueSageCardRailsAvailable } from '../../bluesage/bluesage-job.selectors';
import { maskEpi, maskLast4, toMajorUnits, toMinorUnits } from '../../bluesage/bluesage.util';
import { BlueSageCardFieldsComponent } from '../../bluesage/card-fields/bluesage-card-fields.component';
import { BlueSageTerminalPickerComponent } from '../../bluesage/terminal-picker/bluesage-terminal-picker.component';
import { blueSageTerminalPickerFeature } from '../../bluesage/terminal-picker/bluesage-terminal-picker.feature';
import { BlueSageTerminalPushActions } from '../../bluesage/terminal-push/bluesage-terminal-push.actions';
import { BlueSageTerminalPushComponent } from '../../bluesage/terminal-push/bluesage-terminal-push.component';
import { blueSageTerminalPushFeature } from '../../bluesage/terminal-push/bluesage-terminal-push.feature';
import { SelectOnFocusDirective } from '../../shared/directives/select-on-focus.directive';
import { selectJobCollectibles, selectTransactionJob } from '../transaction-job.selectors';

import { CreateTransactionDialogActions } from './create-transaction-dialog.actions';
import { createTransactionDialogFeature } from './create-transaction-dialog.feature';
import {
    selectCompleteTarget,
    selectCustomerReachable,
    selectDepositOffer,
    selectMoneyGoingOnCard,
    selectNewInvoiceOffer,
    selectSelectedTargets,
} from './create-transaction-dialog.selectors';
import {
    PaymentTypeConfig,
    CreateTransactionMethod,
    CreateTransactionStep,
    CREATE_TRANSACTION_STEPS,
} from './create-transaction-dialog.state';
import {
    CARD_PAYMENT_METHODS,
    otherPaymentTypes,
    resolvePaymentTypeForMethod,
} from './create-transaction-dialog.util';

const PERCENT = 100;

interface MethodTile {
    method: CreateTransactionMethod;
    icon: string;
    // Requirement: a zone may relabel its own payment types, and a tile carries
    // the name that zone chose. This is only what it falls back to when there is
    // no config to read.
    label: string;
    needsCardRail?: boolean;
}

const METHOD_TILES: MethodTile[] = [
    { method: 'terminal', icon: 'pi pi-mobile', label: 'Send to terminal', needsCardRail: true },
    { method: 'preauth', icon: 'pi pi-lock', label: 'Pre-authorize on terminal', needsCardRail: true },
    { method: 'card', icon: 'pi pi-credit-card', label: 'Charge card', needsCardRail: true },
    { method: 'payLink', icon: 'pi pi-link', label: 'Send pay link', needsCardRail: true },
    { method: 'cash', icon: 'pi pi-money-bill', label: 'Cash' },
    { method: 'check', icon: 'pi pi-pencil', label: 'Check' },
    { method: 'recordCard', icon: 'pi pi-clipboard', label: 'Record card payment' },
    { method: 'squareCheckout', icon: 'pi pi-shopping-cart', label: 'Square Checkout' },
];

const CARD_METHOD_TILES: MethodTile[] = [
    { method: 'creditCard', icon: 'pi pi-credit-card', label: 'Credit Card' },
    { method: 'visa', icon: 'pi pi-credit-card', label: 'Visa' },
    { method: 'masterCard', icon: 'pi pi-credit-card', label: 'MasterCard' },
    { method: 'amex', icon: 'pi pi-credit-card', label: 'Amex' },
    { method: 'debit', icon: 'pi pi-credit-card', label: 'Debit' },
];

function resolveMethodTiles(
    tiles: MethodTile[],
    paymentTypes: PaymentTypeConfig[],
    method: CreateTransactionMethod | undefined,
    cardRailsAvailable: boolean,
): (MethodTile & { selected: boolean })[] {
    return tiles.flatMap((tile) => {
        if (tile.needsCardRail && !cardRailsAvailable) { return []; }

        const resolved = resolvePaymentTypeForMethod(paymentTypes, tile.method, tile.label);

        if (!resolved) {
            return [ { ...tile, selected: tile.method === method } ];
        }

        if (!resolved.id) { return []; }

        return [ { ...tile, label: resolved.label, selected: tile.method === method } ];
    });
}

@Component({
    selector: 'app-create-transaction-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        CheckboxModule,
        DialogModule,
        InputNumberModule,
        InputTextModule,
        MessagesModule,
        RadioButtonModule,
        SkeletonModule,
        TooltipModule,
        QuillEditorComponent,
        BlueSageCardFieldsComponent,
        BlueSageTerminalPickerComponent,
        BlueSageTerminalPushComponent,
        SelectOnFocusDirective,
    ],
    providers: [ CurrencyPipe ],
    templateUrl: './create-transaction-dialog.component.html',
    styleUrls: [ './create-transaction-dialog.component.scss' ],
})
export class CreateTransactionDialogComponent {

    actions = CreateTransactionDialogActions;
    pushActions = BlueSageTerminalPushActions;

    steps = CREATE_TRANSACTION_STEPS;

    loadingTargetTiles = [ 1, 2 ];

    remarksModules: QuillModules = {
        toolbar: [
            [ 'bold', 'italic', 'underline' ],
            [ { list: 'ordered' }, { list: 'bullet' } ],
            [ 'clean' ],
        ],
    };

    viewModel$ = this.store.select(createSelector({
        open: createTransactionDialogFeature.selectOpen,
        step: createTransactionDialogFeature.selectStep,
        creating: createTransactionDialogFeature.selectCreating,
        error: createTransactionDialogFeature.selectError,

        // The job's currency. The currency pipe shows USD as "$" and CAD as
        // "CA$", so a Canadian job is never mistaken for a US one.
        currency: createTransactionDialogFeature.selectCurrency,

        stepNumber: createSelector(
            createTransactionDialogFeature.selectStep,
            (step) => (step === 'events' ? 1 : CREATE_TRANSACTION_STEPS.indexOf(step) + 1),
        ),

        jobLoading: createSelector(
            selectTransactionJob,
            createTransactionDialogFeature.selectJobLoadFailed,
            (job, failed) => !job && !failed,
        ),

        jobLoadFailed: createSelector(
            selectTransactionJob,
            createTransactionDialogFeature.selectJobLoadFailed,
            (job, failed) => failed && !job,
        ),

        // Requirement: the deposit tile is offered on every job, whether or not
        // a deposit is required — a customer paying up front is ordinary.
        targetTiles: createSelector(
            selectJobCollectibles,
            selectDepositOffer,
            selectNewInvoiceOffer,
            createTransactionDialogFeature.selectTarget,
            (collectibles, depositOffer, newInvoiceOffer, target) => [
                ...(collectibles?.openInvoices ?? []).map((invoice) => ({
                    action: CreateTransactionDialogActions.targetTileClicked({
                        target: { kind: 'invoice', invoiceId: invoice.id },
                    }) as Action,
                    icon: 'pi pi-file',
                    label: `#${ invoice.invoiceNumber }`,
                    dueDate: invoice.dueDate as number | undefined,
                    amount: toMajorUnits(invoice.balanceMinor),
                    selected: target?.kind === 'invoice' && target.invoiceId === invoice.id,
                })),
                ...(newInvoiceOffer ? [ {
                    action: CreateTransactionDialogActions.newInvoiceTileClicked({
                        eventIds: newInvoiceOffer.defaultEventIds,
                        askForEvents: newInvoiceOffer.askForEvents,
                    }) as Action,
                    icon: 'pi pi-file-plus',
                    label: 'New invoice',
                    dueDate: undefined as number | undefined,
                    amount: toMajorUnits(newInvoiceOffer.balanceMinor),
                    selected: target?.kind === 'newInvoice',
                } ] : []),
                {
                    action: CreateTransactionDialogActions.targetTileClicked({
                        target: { kind: 'deposit' },
                    }) as Action,
                    icon: 'pi pi-wallet',
                    label: 'Deposit',
                    dueDate: undefined as number | undefined,
                    amount: toMajorUnits(depositOffer.amountMinor),
                    selected: target?.kind === 'deposit',
                },
            ],
        ),

        invoiceEventTiles: createSelector(
            selectNewInvoiceOffer,
            createTransactionDialogFeature.selectNewInvoiceEventIds,
            (offer, chosenEventIds) => (offer?.events ?? []).map((event) => ({
                action: CreateTransactionDialogActions.invoiceEventTileClicked({
                    eventId: event.id,
                    singleSelect: offer.singleSelect,
                }),
                eventId: event.id,
                label: event.title,
                start: event.start,
                amount: toMajorUnits(event.totalMinor),
                selected: chosenEventIds.includes(event.id),
            })),
        ),

        invoiceEventsSingleSelect: createSelector(
            selectNewInvoiceOffer,
            (offer) => Boolean(offer?.singleSelect),
        ),

        canContinueToAmount: createSelector(
            selectSelectedTargets,
            (targets) => Boolean(targets.newInvoiceEventIds?.length) && targets.totalMinor > 0,
        ),

        amountOverBalance: createSelector(
            selectSelectedTargets,
            createTransactionDialogFeature.selectAmountMinor,
            (targets, amountMinor) => Boolean(targets.invoice || targets.newInvoiceEventIds)
                && amountMinor > targets.totalMinor,
        ),

        invoiceEmptyHint: createSelector(
            selectJobCollectibles,
            selectNewInvoiceOffer,
            (collectibles, newInvoiceOffer) => {
                if (!collectibles || newInvoiceOffer) { return undefined; }

                if ((collectibles.openInvoices ?? []).length) { return undefined; }

                return collectibles.hasAnyInvoices
                    ? 'Every invoice on this job is already settled — nothing left to collect.'
                    : 'This job has no invoices.';
            },
        ),

        selectedTotal: createSelector(
            selectSelectedTargets,
            (targets) => toMajorUnits(targets.totalMinor),
        ),

        selectedTotalLabel: createSelector(
            selectSelectedTargets,
            (targets) => {
                if (targets.depositSelected) { return 'Deposit'; }

                return targets.invoice || targets.newInvoiceEventIds ? 'Balance' : undefined;
            },
        ),

        selectedTotalMinor: createSelector(
            selectSelectedTargets,
            (targets) => targets.totalMinor,
        ),

        // Requirement: no route back while a pay link is live. The method grid
        // can charge a card while that link is still payable, which takes the
        // money twice.
        collecting: createSelector(
            createTransactionDialogFeature.selectStep,
            createTransactionDialogFeature.selectAmountMinor,
            createTransactionDialogFeature.selectMethod,
            createTransactionDialogFeature.selectEmbedUrl,
            createTransactionDialogFeature.selectPayUrl,
            selectSelectedTargets,
            createTransactionDialogFeature.selectCurrency,
            (step, amountMinor, method, embedUrl, payUrl, targets, currency) => {
                if (step === 'holds' || step === 'targets' || step === 'events' || step === 'complete') {
                    return undefined;
                }

                const invoiceLabel = targets.invoice
                    ? `Invoice #${ targets.invoice.invoiceNumber }`
                    : targets.newInvoiceSelected && 'New invoice';

                const target = targets.depositSelected ? 'Deposit' : invoiceLabel;

                if (!target) { return undefined; }

                const mayGoBack = !embedUrl && !(method === 'payLink' && payUrl);

                const linkTo = (from: CreateTransactionStep) =>
                    (mayGoBack && step !== from ? from : undefined);

                return {
                    target: {
                        text: target,
                        step: linkTo('targets'),
                        action: 'Change what this is collected against',
                    },
                    amount: {
                        text: this.currency.transform(toMajorUnits(amountMinor), currency) ?? '',
                        step: linkTo('amount'),
                        action: 'Change the amount',
                    },
                };
            },
        ),

        amount: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            toMajorUnits,
        ),
        amountMinor: createTransactionDialogFeature.selectAmountMinor,

        // Requirement: the quote must match what the device will take, so it
        // rounds the way the middleware rounds. It is quoted for display only —
        // no differential is ever written to the CRM.
        cardPrice: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            createTransactionDialogFeature.selectDifferentialPct,
            createTransactionDialogFeature.selectDifferentialPctKnown,
            selectMoneyGoingOnCard,
            (amountMinor, pct, known, onCard) => {
                if (!onCard || !known || !pct || amountMinor <= 0) { return undefined; }

                const differentialMinor = Math.round((amountMinor * pct) / PERCENT);

                return { total: toMajorUnits(amountMinor + differentialMinor) };
            },
        ),

        method: createTransactionDialogFeature.selectMethod,

        device: createSelector(
            blueSageTerminalPickerFeature.selectSelectedEpi,
            (epi) => maskEpi(epi),
        ),

        embedUrl: createTransactionDialogFeature.selectEmbedUrl,
        openingCardForm: createTransactionDialogFeature.selectOpeningCardForm,
        saveCard: createTransactionDialogFeature.selectSaveCard,
        cardsOnFile: createTransactionDialogFeature.selectCardsOnFile,

        newCard: createSelector(
            createTransactionDialogFeature.selectCardToken,
            createTransactionDialogFeature.selectCardLast4,
            (cardToken, last4) => (cardToken
                ? { label: last4 ? `New card ${ maskLast4(last4) }` : 'New card' }
                : undefined),
        ),

        selectedOnFileToken: createSelector(
            createTransactionDialogFeature.selectSelectedCard,
            (selected) => (selected?.kind === 'onFile' ? selected.token : undefined),
        ),
        newCardSelected: createSelector(
            createTransactionDialogFeature.selectSelectedCard,
            (selected) => selected?.kind === 'new',
        ),

        paymentTypesFailed: createTransactionDialogFeature.selectPaymentTypesFailed,
        paymentTypesLoaded: createTransactionDialogFeature.selectPaymentTypesLoaded,
        selectedPaymentTypeId: createTransactionDialogFeature.selectSelectedPaymentTypeId,

        paymentTypeTiles: createSelector(
            createTransactionDialogFeature.selectPaymentTypes,
            createTransactionDialogFeature.selectMethod,
            createTransactionDialogFeature.selectSelectedPaymentTypeId,
            (paymentTypes, method, selectedPaymentTypeId) =>
                otherPaymentTypes(paymentTypes).map((type) => ({
                    paymentTypeId: type.id,
                    label: type.name,
                    selected: method === 'other' && type.id === selectedPaymentTypeId,
                })),
        ),

        // Requirement: a payment type misconfigured with a provider is still
        // offered. The backend's own refusal is what gets the zone's settings
        // fixed; a tile that quietly vanished would absorb it.
        methods: createSelector(
            createTransactionDialogFeature.selectPaymentTypes,
            createTransactionDialogFeature.selectMethod,
            selectBlueSageCardRailsAvailable,
            (paymentTypes, method, cardRailsAvailable) => {
                const cardMethods = resolveMethodTiles(
                    CARD_METHOD_TILES, paymentTypes, method, cardRailsAvailable,
                );

                return resolveMethodTiles(METHOD_TILES, paymentTypes, method, cardRailsAvailable)
                    .filter((tile) => tile.method !== 'recordCard' || cardMethods.length > 0);
            },
        ),

        cardMethods: createSelector(
            createTransactionDialogFeature.selectPaymentTypes,
            createTransactionDialogFeature.selectMethod,
            (paymentTypes, method) => resolveMethodTiles(
                CARD_METHOD_TILES, paymentTypes, method, true,
            ),
        ),

        isRecordingManualPayment: createSelector(
            createTransactionDialogFeature.selectMethod,
            (method) => method === 'cash'
                || method === 'check'
                || method === 'other'
                || Boolean(method && CARD_PAYMENT_METHODS.includes(method)),
        ),

        methodPrompt: createSelector(
            createTransactionDialogFeature.selectMethod,
            (method) => {
                switch (method) {
                    case 'terminal':
                    case 'preauth': return 'What terminal do you want to use?';
                    case 'card': return 'What card do you want to use?';
                    case 'recordCard': return 'What type of card did they use?';
                    case 'squareCheckout': return 'Send a Square Checkout link to the customer';
                    default: return 'Enter additional details';
                }
            },
        ),

        recordLabel: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            createTransactionDialogFeature.selectPaymentTypes,
            createTransactionDialogFeature.selectSelectedPaymentTypeId,
            createTransactionDialogFeature.selectCurrency,
            (amountMinor, paymentTypes, paymentTypeId, currency) => {
                const amount = this.currency.transform(toMajorUnits(amountMinor), currency);
                const type = paymentTypes.find((option) => option.id === paymentTypeId)?.name;

                return [ 'Record', amount, type ].filter(Boolean).join(' ');
            },
        ),

        remarks: createTransactionDialogFeature.selectRemarks,
        sendReceipt: createTransactionDialogFeature.selectSendReceipt,

        // Requirement: a pay link carries the receipt answer it was minted
        // with, so the toggle is gone once one has been sent.
        //
        // Square never gets this toggle at all — the Square webhook hardcodes
        // `disableSendReceipt: true` when it settles the transaction, so the
        // toggle would offer a choice the backend doesn't honor.
        receiptChoice: createSelector(
            createTransactionDialogFeature.selectMethod,
            createTransactionDialogFeature.selectEmbedUrl,
            createTransactionDialogFeature.selectPayUrl,
            selectCustomerReachable,
            (method, embedUrl, payUrl, reachable) => {
                if (!method || method === 'recordCard' || method === 'squareCheckout'
                    || embedUrl || payUrl) { return 'none'; }
                if (reachable) { return 'toggle'; }

                return method === 'payLink' ? 'none' : 'unavailable';
            },
        ),

        payUrl: createTransactionDialogFeature.selectPayUrl,
        vaultOnPayLink: createTransactionDialogFeature.selectVaultOnPayLink,

        // Claims only what the customer record shows. The SMS is attempted
        // regardless, because Send SMS to Customer resolves additional contacts
        // this cannot see.
        customerUnreachable: createSelector(
            selectCustomerReachable,
            (reachable) => !reachable,
        ),

        canCharge: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            createTransactionDialogFeature.selectSelectedCard,
            createTransactionDialogFeature.selectCardToken,
            createTransactionDialogFeature.selectCreating,
            createTransactionDialogFeature.selectPaymentTypeId,
            (amountMinor, selected, cardToken, creating, paymentTypeId) => {
                if (creating || amountMinor <= 0 || !selected || !paymentTypeId) { return false; }

                return selected.kind === 'onFile' || Boolean(cardToken);
            },
        ),

        // Requirement: recording a payment needs no per-method extra. Nothing
        // reads a check number back, so demanding one only blocks a real payment.
        canRecord: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            createTransactionDialogFeature.selectSelectedPaymentTypeId,
            createTransactionDialogFeature.selectCreating,
            (amountMinor, paymentTypeId, creating) =>
                !creating && amountMinor > 0 && Boolean(paymentTypeId),
        ),

        canSendPayLink: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            createTransactionDialogFeature.selectPaymentTypeId,
            createTransactionDialogFeature.selectCreating,
            (amountMinor, paymentTypeId, creating) =>
                !creating && amountMinor > 0 && Boolean(paymentTypeId),
        ),

        // Requirement: not gated on the customer being reachable, same as
        // `canSendPayLink` — an unreachable customer still gets a transaction
        // created, and the link lands in that transaction's remarks for the
        // operator to send by hand instead of by SMS/email.
        canSendSquareCheckout: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            createTransactionDialogFeature.selectSelectedPaymentTypeId,
            createTransactionDialogFeature.selectCreating,
            (amountMinor, paymentTypeId, creating) =>
                !creating && amountMinor > 0 && Boolean(paymentTypeId),
        ),

        // Requirement: Send is never disabled on a terminal's status reading —
        // the roster may be a minute old and the middleware is the authority at
        // push time. The payment type is in the gate because the dialog re-reads
        // the zone's setup as it opens, and a press inside that window files a
        // configuration fault for what is only a race.
        canSend: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            blueSageTerminalPickerFeature.selectSelectedEpi,
            createTransactionDialogFeature.selectCreating,
            createTransactionDialogFeature.selectPaymentTypeId,
            (amountMinor, epi, creating, paymentTypeId) =>
                !creating && amountMinor > 0 && Boolean(epi) && Boolean(paymentTypeId),
        ),

        // Requirement: the holds are read live from the job, never from this
        // dialog's own state. A dialog left standing while a hold is settled
        // elsewhere would otherwise offer an id whose release now voids an
        // approved payment.
        holdTiles: createSelector(
            selectJobCollectibles,
            (collectibles) => (collectibles?.openPreauths ?? []).map((hold) => ({
                transactionId: hold.id,
                amountMinor: hold.amountMinor,
                amount: toMajorUnits(hold.amountMinor),
                label: hold.epi ? `Terminal ${ maskEpi(hold.epi) }` : 'Authorization',
            })),
        ),

        holdsPrompt: createSelector(
            selectJobCollectibles,
            (collectibles) => ((collectibles?.openPreauths ?? []).length > 1
                ? 'This job is holding these authorizations. Do you want to complete one?'
                : 'This job is holding this authorization. Do you want to complete it?'),
        ),

        openPreauthTransactionId: createSelector(
            selectCompleteTarget,
            (target) => target?.id,
        ),

        heldAmount: createSelector(
            selectCompleteTarget,
            (target) => toMajorUnits(target?.amountMinor ?? 0),
        ),

        balance: createSelector(
            selectJobCollectibles,
            (collectibles) => toMajorUnits(collectibles?.balanceMinor ?? 0),
        ),

        completeAmountExceedsBalance: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            selectJobCollectibles,
            (amountMinor, collectibles) => Boolean(collectibles) && amountMinor > (collectibles?.balanceMinor ?? 0),
        ),

        nothingOwing: createSelector(
            selectJobCollectibles,
            (collectibles) => Boolean(collectibles) && (collectibles?.balanceMinor ?? 0) <= 0,
        ),

        // Requirement: completing above the hold is allowed but never the
        // default — the excess is not covered by the authorization, and the
        // issuer may decline it.
        completeAmountExceedsHold: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            selectCompleteTarget,
            (amountMinor, target) => amountMinor > (target?.amountMinor ?? 0),
        ),

        balanceExceedsHold: createSelector(
            selectJobCollectibles,
            selectCompleteTarget,
            (collectibles, target) => (collectibles?.balanceMinor ?? 0) > (target?.amountMinor ?? 0),
        ),

        completeShortfall: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            selectJobCollectibles,
            (amountMinor, collectibles) =>
                toMajorUnits(Math.max(0, (collectibles?.balanceMinor ?? 0) - amountMinor)),
        ),

        canComplete: createSelector(
            createTransactionDialogFeature.selectAmountMinor,
            selectJobCollectibles,
            selectCompleteTarget,
            blueSageTerminalPickerFeature.selectSelectedEpi,
            createTransactionDialogFeature.selectCreating,
            (amountMinor, collectibles, target, pickedEpi, creating) => !creating
                && amountMinor > 0
                && amountMinor <= (collectibles?.balanceMinor ?? 0)
                && Boolean(target?.id)
                && Boolean(target?.epi ?? pickedEpi),
        ),

        // Requirement: a completion can only go to the terminal the
        // authorization was taken on, so the hold's own device wins over
        // whatever the picker has selected.
        holdDevice: createSelector(
            selectCompleteTarget,
            (target) => maskEpi(target?.epi),
        ),

        selectedEpi: createSelector(
            selectCompleteTarget,
            blueSageTerminalPickerFeature.selectSelectedEpi,
            (target, pickedEpi) => target?.epi ?? pickedEpi,
        ),

        completionDeviceOffline: createSelector(
            blueSageTerminalPickerFeature.selectTerminals,
            selectCompleteTarget,
            blueSageTerminalPickerFeature.selectSelectedEpi,
            (terminals, target, selectedEpi) =>
                terminals.find((t) => t.epi === (target?.epi ?? selectedEpi))
                    ?.status === 'offline',
        ),

        // Requirement: while any money action is in flight, nothing else on the
        // dialog may be pressed. Release hold and Complete authorization move a
        // customer's held money and must not stay live during a charge.
        busy: createSelector(
            createTransactionDialogFeature.selectCreating,
            (creating) => creating,
        ),

        pushInFlight: createSelector(
            blueSageTerminalPushFeature.selectPhase,
            (phase) => phase !== 'idle',
        ),

        // Requirement: the dialog may not be dismissed while an amount is live
        // on a reader — a stray click beside it would close the only screen
        // reporting what the customer is doing.
        pushLive: createSelector(
            blueSageTerminalPushFeature.selectPhase,
            (phase) => phase === 'preparing' || phase === 'sending' || phase === 'waiting',
        ),
    }));

    constructor(
        public store: Store,
        private currency: CurrencyPipe,
    ) {}

    toMinor = toMinorUnits;

}
